/**
 * TEAMS & ROLES (condensed mirror — canonical note: see above
 * isOrgConsoleProfile in serverWorkspaces.ts):
 * WORKSPACE TEAM = the workspace's own internal staff ONLY
 * (users.workspaceMembers[workspaceId]); ORGANIZATION TEAMS = a
 * referrer's / employer's / sponsor's own people (the org doc's
 * linkedUsers[], agentRole 'super' | 'regular'), managed only in the
 * org's own dashboard. The workspaceMembers 'admin' stamp written on
 * org approval denotes ORG OWNERSHIP, not staff adminship. This module
 * resolves client module access for the WORKSPACE TEAM only.
 */
export type ModuleAccessLevel = 'none' | 'view' | 'edit';

const VIEW_ONLY_MEMBER_MODULES = ['applicants', 'events', 'my-events', 'tickets', 'workflow', 'automations'];
const EXTERNAL_USER_ROLES = new Set([
  'applicant',
  'employer',
  'referrer',
  'enhanced_referrer',
  'sponsor',
  'agent',
]);
const INTERNAL_TEAM_ROLES = new Set([
  'admin',
  'super_admin',
  'super',
  'owner',
  'master',
  'manager',
  'member',
  'team_member',
  'viewer',
  'staff',
  'custom',
]);

/**
 * Normalize a role value to a lower-cased string. The server may hand back an
 * object-shaped membership ({ role: 'admin', addedAt }) from legacy data via
 * `profile.workspaceRole`; a raw `.toLowerCase()` on that object would throw
 * and crash the dashboard module gate. Treat both shapes identically.
 */
function roleName(value: any): string {
  if (!value) return '';
  const raw = typeof value === 'string' ? value : (value?.role ?? value?.name ?? '');
  return String(raw || '').toLowerCase();
}

/**
 * Returns true if the role string represents an internal workspace team member
 * (e.g. admin, manager, member, custom workspace role) rather than an external user.
 */
export function isWorkspaceTeamRole(role: any): boolean {
  const r = roleName(role);
  if (!r) return false;
  if (EXTERNAL_USER_ROLES.has(r)) return false;
  if (INTERNAL_TEAM_ROLES.has(r)) return true;
  // Any custom role that is not explicitly an external user role is considered an internal workspace team role.
  return true;
}

/** True for platform / workspace super-admin tiers that bypass module checks. */
function isSuperAdmin(profile: any): boolean {
  if (!profile) return false;
  const wsRole = roleName(profile?.workspaceRole);
  const profileRole = roleName(profile?.role);
  const adminRoles = new Set(['master', 'owner', 'super', 'platform_admin']);

  // If a specific workspace role is present, it takes precedence.
  // A team member with wsRole 'member', 'team_member', or 'viewer' is never a super admin.
  if (wsRole) {
    return adminRoles.has(wsRole);
  }

  return (
    adminRoles.has(profileRole) ||
    profile?.platformAdmin === true || profile?.isPlatformAdmin === true || profile?.isSuperAdmin === true
  );
}

/**
 * Built-in workspace role rules — mirrors the server's `workspacePermissionLevel`
 * matrix exactly (see server.ts):
 *   Admin   → every module (full access)
 *   Manager → every module EXCEPT Settings
 *   Member  → view-only on Applicants / Events / My Events / Support Tickets
 *   Viewer  → nothing
 * Custom/unknown roles return `null` so the caller can fall through to the
 * stored per-role permission matrix (or deny).
 */
function builtInRoleLevel(role: string | undefined, tabId: string): ModuleAccessLevel | null {
  switch ((role || '').toLowerCase()) {
    case 'admin':
    case 'owner':
    case 'super':
    case 'master':
      return 'edit';
    case 'manager':
      return tabId === 'settings' ? 'none' : 'edit';
    case 'member':
    case 'team_member':
      return VIEW_ONLY_MEMBER_MODULES.includes(tabId) ? 'view' : 'none';
    case 'viewer':
      return 'none';
    default:
      return null;
  }
}

/**
 * Resolve the effective module permission level ('none' | 'view' | 'edit') for
 * the given workspace team-member tab/module. This is the single client-side
 * source of truth used by the sidebar, tab gate, dashboards and widget level:
 *
 *   No Access  → module hidden everywhere, never rendered, never actionable.
 *   View Only  → visible + readable, every mutation path disabled.
 *   View & Edit → visible + fully editable.
 *
 * Resolution order (mirrors the server's effective-identity grants):
 *   1. The dashboard itself is always reachable.
 *   2. The `workspaces` tab is reserved for platform super-admins.
 *   3. Super-admins bypass module checks.
 *   4. The server-computed `profile.permissions` matrix is authoritative when
 *      present — a module stored as `none` is never granted via a role rule.
 *   5. Built-in workspace role rules are the fallback while the effective
 *      permissions matrix is still loading (identical to the server matrix),
 *      so the UI never flashes a module the member cannot access.
 *   6. Unknown roles / custom roles without a matching stored permission are
 *      denied (fail closed).
 */
export function moduleAccessLevel(tabId: string, profile: any, isPlatformAdmin?: boolean): ModuleAccessLevel {
  if (!tabId || tabId === 'dashboard') return 'edit';

  const role = roleName(profile?.workspaceRole || profile?.role);

  // Hard gate: External roles MUST NEVER access Workspace Settings under any circumstance,
  // even if a stale/misconfigured permissions object grants it or isSuperAdmin matches.
  if (tabId === 'settings' && EXTERNAL_USER_ROLES.has(role)) {
    return 'none';
  }

  // The Workspaces tab is strictly reserved for platform super admins.
  if (tabId === 'workspaces') return isPlatformAdmin ? 'edit' : 'none';
  if (isPlatformAdmin || isSuperAdmin(profile)) return 'edit';

  // The server-computed permissions matrix is the single source of truth.
  const perms = profile?.permissions;
  if (perms && typeof perms === 'object') {
    const perm = perms[tabId];
    if (perm === 'none') return 'none';
    if (perm === 'view' || perm === 'edit') {
      // Hard gate: non-admin team members can never access Workspace Settings
      if (tabId === 'settings' && !['admin', 'owner', 'super', 'master'].includes(role)) {
        return 'none';
      }
      return perm;
    }
    // Module absent from the matrix: non-admin roles must fail closed to 'none'.
    if (!['admin', 'owner', 'super', 'master', 'manager'].includes(role)) {
      return 'none';
    }
  }

  const builtIn = builtInRoleLevel(role, tabId);
  if (builtIn) return builtIn;

  // Fail closed for unknown/custom roles without explicit grants.
  return 'none';
}

/**
 * Module access permission resolver for workspace roles & custom roles.
 * True when the member can VIEW the module (No Access is the only denial).
 */
export function hasModuleAccess(tabId: string, profile: any, isPlatformAdmin?: boolean): boolean {
  return moduleAccessLevel(tabId, profile, isPlatformAdmin) !== 'none';
}

/** True when the member can view the given module/page. */
export function canViewModule(tabId: string, profile: any, isPlatformAdmin?: boolean): boolean {
  return moduleAccessLevel(tabId, profile, isPlatformAdmin) !== 'none';
}

/** True when the member can EDIT (mutate) the given module/page. */
export function canEditModule(tabId: string, profile: any, isPlatformAdmin?: boolean): boolean {
  return moduleAccessLevel(tabId, profile, isPlatformAdmin) === 'edit';
}

/** Workspace context the Workspace Settings visibility predicate reads. */
export interface WorkspaceSettingsGateContext {
  /** useWorkspaces().platformAdmin for the signed-in user. */
  platformAdmin?: boolean;
  /** useWorkspaces().activeWorkspace?.role — the membership role of the active workspace. */
  activeWorkspaceRole?: unknown;
  /** useWorkspaces().loading — while true, memberships are still resolving. */
  workspacesLoading?: boolean;
}

/**
 * Workspace Settings visibility — the SINGLE definition, consumed by every
 * shell's profile menu (the shared UserMenu and the admin dashboard's own
 * dropdown) so the rule can never drift between menus.
 *
 * Workspace Settings gets a deny-by-default treatment stricter than
 * Backend Tasks. isWorkspaceAdmin is satisfied by the org-ownership stamp
 * (workspaceMembers[ws]='admin', written by mutateUserRole on org
 * approval) for EVERY approved org owner, and hasModuleAccess resolves
 * the role as profile.workspaceRole || profile.role, where App.tsx has
 * already derived workspaceRole='admin' from that same stamp — so the
 * settings hard gate in moduleAccessLevel never fires for them either.
 * The owner ruling is absolute: external org accounts must never see or
 * open the MAIN workspace's settings (their own org surfaces live in
 * their dashboards). Discriminate exactly like the server does
 * (isOrgConsoleProfile: primaryRole first, then role) and deny when
 * EITHER source names an org role, so a legacy admin custom claim
 * flipping the derived profile.role to 'admin' cannot resurrect the
 * item. Post-resolution only: while workspace memberships are still
 * loading, the item never renders (no permissive default during the
 * unresolved window). And the session itself must be an admin session:
 * a dual-role account (e.g. a workspace admin who is also an applicant)
 * keeps its derived workspaceRole while sitting in a non-admin shell, so
 * capability alone would surface the item there. App.tsx stamps
 * profile.role from the ACTIVE session role ('applicant' in the
 * applicant shell, the org role in an org shell, 'admin'/'platform_admin'
 * in the team shell) — require an admin-tier session role so the rule is
 * identical in every shell.
 *
 * TEAMS & ROLES: this gate interprets the org-ownership stamp — it must
 * never grant org accounts the workspace team's settings — see the
 * canonical note above isOrgConsoleProfile in serverWorkspaces.ts.
 */
export function canSeeWorkspaceSettings(profile: any, ctx: WorkspaceSettingsGateContext): boolean {
  const platformAdmin = ctx?.platformAdmin;
  const activeRole = String(ctx?.activeWorkspaceRole || '').toLowerCase();
  const isWorkspaceAdmin =
    (platformAdmin || ['admin', 'owner', 'super', 'master'].includes(activeRole)) &&
    hasModuleAccess('settings', profile, platformAdmin);
  const primaryRole = String(profile?.role || '').toLowerCase();
  const isExternalOrgAccount = ['referrer', 'employer', 'sponsor'].includes(primaryRole);
  const orgProfileRole = String(profile?.primaryRole || profile?.role || '').toLowerCase();
  const isExternalOrgProfile = ['referrer', 'employer', 'sponsor', 'enhanced_referrer'].includes(orgProfileRole);
  const isAdminSessionRole = ['admin', 'platform_admin', 'owner', 'super', 'master'].includes(primaryRole);
  return !ctx?.workspacesLoading && isWorkspaceAdmin && !isExternalOrgAccount && !isExternalOrgProfile && isAdminSessionRole;
}

/**
 * Resolve the effective level ('none' | 'view' | 'edit') for a fine-grained
 * applicant action (`applicant.*` key). The server's effective-identity
 * endpoint computes these levels for every role (workspace admins → 'edit',
 * built-in members → derived from the Applicants module, custom roles → their
 * stored sub-permission), so the client only needs to read `profile.permissions`.
 */
export function applicantActionLevel(profile: any, action: string): 'none' | 'view' | 'edit' {
  if (!profile) return 'none';
  const role = roleName(profile?.workspaceRole || profile?.role);
  // Safety net for stale permission snapshots: built-in admin tiers keep full
  // applicant access even if the cached `permissions` map lacks the new keys.
  if (profile?.platformAdmin || profile?.isPlatformAdmin) return 'edit';
  if (role === 'admin' || role === 'manager' || role === 'owner' || role === 'super' || role === 'master') return 'edit';
  const p = profile?.permissions?.[action];
  return p === 'view' || p === 'edit' ? p : 'none';
}

/** Convenience boolean wrapper around {@link applicantActionLevel}. */
export function hasApplicantAction(profile: any, action: string, level: 'view' | 'edit' = 'edit'): boolean {
  const granted = applicantActionLevel(profile, action);
  return level === 'edit' ? granted === 'edit' : granted !== 'none';
}

/** Check whether a candidate record falls within the user's record visibility scope (Axis B). */
export function isApplicantInScope(app: any, profile: any, currentUid?: string): boolean {
  if (!app || !profile) return true;
  const role = roleName(profile?.workspaceRole || profile?.role);
  if (['admin', 'owner', 'super', 'master', 'platform_admin'].includes(role) || profile?.isSuperAdmin || profile?.platformAdmin) {
    return true;
  }
  const scope = profile?.permissions?.applicantsScope || 'all';
  if (scope !== 'assigned') return true;

  const uid = currentUid || profile?.uid || profile?.id;
  const email = (profile?.email || '').toLowerCase();

  const assignedMemberIds = Array.isArray(app.assignedTeamMemberIds) ? app.assignedTeamMemberIds : [];
  if (assignedMemberIds.includes(uid)) return true;
  if (app.assignedAgentId === uid || app.assignedTo === uid || app.assignedToUid === uid || app.teamMemberId === uid) return true;
  if (email && (app.assignedToEmail === email || app.userEmail === email)) return true;

  return false;
}
